import { Component, Injector, Input, OnInit } from '@angular/core';
import { DateUtils } from '../../../../utils/date-utils';
import { TableUtilsService, TableColumn, AttributeType, PaginationInfo, ViewStatus, QuickActionsTableComponent, ColumnSorting } from '@uranda/ui';
import { LeadSource, LeadStatus, Lead, VerificationFilter } from '../../../../models/lead.model';
import { ControlPanelService } from '../control-panel.service';
import { AdminService } from '../services/admin.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LeadDetailsComponent } from './lead-details/lead-details.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginationService } from '../services/pagination.service';
import { BadgeTableComponent, AvailableBadgeStatus } from '../../components/BadgeTableComponent/BadgeTableComponent.component';

@Component({
  selector: 'app-verified-icon',
  template: `
  <div style="width: fit-content;" class="d-flex ms-2 align-items-center justify-content-center">
    <i
      [ngClass]="isVerified ? 'ti ti-circle-check text-success' : 'ti ti-circle-x text-muted'"
      style="font-size: 22px">
    </i></div>
  `,
  standalone: false
})
export class VerifiedIconComponent {
  @Input() data!: Lead;

  @Input() verifiedAt?: string;

  get isVerified(): boolean {
    return this.data == null ? !!this.verifiedAt : !!(this.data.phoneNumberVerifiedAt);
  }
}

@Component({
  selector: 'app-lead-phone-table',
  template: `
    <div class="d-flex align-items-center">
      <i [class]="iconClass" style="font-size: 22px"></i>
      <span>{{ data.phoneNumber }}</span>
    </div>
  `,
  standalone: false
})
export class LeadPhoneTableComponent {
  @Input() data!: Lead;

  get iconClass(): string {
    return this.data?.phoneNumberVerifiedAt
      ? 'ti ti-circle-check text-success me-1'
      : 'ti ti-circle-x text-muted me-1';
  }
}

@Component({
  selector: 'app-lead-email-table',
  template: `
    <div *ngIf="data?.email" class="d-flex align-items-center">
      <i [class]="iconClass" style="font-size: 22px"></i>
      <span>{{ data.email }}</span>
    </div>
  `,
  standalone: false
})
export class LeadEmailTableComponent {
  @Input() data!: Lead;

  get iconClass(): string {
    return this.data?.emailVerifiedAt
      ? 'ti ti-circle-check text-success me-1'
      : 'ti ti-circle-x text-muted me-1';
  }
}

@Component({
  selector: 'app-lead-name',
  template: `
  <div style="width: fit-content;" [ngClass]="{'text-danger':data.disabledAt}"
       class="d-flex align-items-center justify-content-start"
       [ngbTooltip]="data.disabledAt ? 'Lead disabled on ' + (data.disabledAt | date:'dd/MM/yyyy HH:mm':'':'en-US') : ''"
       placement="top">
     <span>{{data | leadName}}</span>
     <i *ngIf="data.disabledAt"
        class="ti ti-ban text-danger ms-2"
        style="font-size: 18px"></i>
    </div>
  `,
  standalone: false
})
export class LeadNameComponent {
  @Input() data!: Lead;

}

@Component({
  selector: 'app-leads',
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.css'],
  standalone: false
})
export class LeadsComponent implements OnInit {
  constructor(
    private injector: Injector,
    private tableUtils: TableUtilsService,
    private controlPanelService: ControlPanelService,
    private adminService: AdminService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private paginationService: PaginationService
  ) {
    this.isAdmin = this.controlPanelService.isAdminUser;
  }

  protected readonly LeadStatus = LeadStatus;
  protected readonly LeadSource = LeadSource;
  columns: TableColumn[] = [];
  leads: any[] = [];
  leadsPaginationInfo = {} as PaginationInfo;
  isAdmin = false;
  activeNav = 0;
  ViewStatus = ViewStatus;
  viewStatus = ViewStatus.LOADING;
  currentPage?: any;
  pageSize: number = 10;
  searchQuery?: string = undefined;
  currentSort?: ColumnSorting[];
  selectedLeadSource?: LeadSource;

  readonly leadSourceOptions: { label: string; value: LeadSource | '' }[] = [
    { label: 'All', value: '' },
    { label: 'Website', value: LeadSource.WEBSITE },
    { label: 'Facebook', value: LeadSource.FACEBOOK },
  ];

  async ngOnInit() {
    this.setColumns();

    this.route.queryParams.subscribe(params => {
      const initialPagination = {
        page: params['page'] ? +params['page'] : 0,
        pageSize: params['pageSize'] ? +params['pageSize'] : 10,
        query: params['searchQuery'] || undefined
      };

      this.activeNav = params['tab'] ? parseInt(params['tab'], 10) : 0;
      this.selectedLeadSource = (params['leadSource'] as LeadSource) || undefined;

      this.paginationService.setPagination(initialPagination);
    });

    this.paginationService.pagination$.subscribe(async p => {
      this.currentPage = p.page;
      this.pageSize = p.pageSize;
      this.searchQuery = p.query;
    });
    const filters = this.getFiltersFromTab(this.activeNav);

    await this.getLeads(this.currentPage, filters);
    this.viewStatus = ViewStatus.READY;
  }

  ngOnDestroy() {
    this.paginationService.setPagination({ page: 0, pageSize: 10, query: '' })
  }

  onSortingChanged(sorting: ColumnSorting[]) {
    this.currentSort = sorting;
    this.getLeads(this.currentPage, this.getFiltersFromTab(this.activeNav));
  }

  async onPageChange(page: number) {
    const contentElement = document.querySelector('.content');
    if (contentElement) {
      contentElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
    await this.getLeads(page, this.getFiltersFromTab(this.activeNav));
  }

  async onPaginationLoad(event: { page: number, pageSize: number; query?: string }) {
    this.paginationService.updatePagination(event);
    await this.getLeads(this.currentPage, this.getFiltersFromTab(this.activeNav));
  }

  ngAfterViewInit(): void {
    this.controlPanelService.currentPageName = 'Leads';
  }

  onLeadSourceChange() {
    this.getLeads(0, this.getFiltersFromTab(this.activeNav));
  }

  getFiltersFromTab(tab: number): { verificationStatus?: VerificationFilter; leadSource?: LeadSource } {
    const leadSource = this.selectedLeadSource || undefined;
    switch (tab) {
      case 0: return { leadSource };
      case 1: return { verificationStatus: VerificationFilter.VERIFIED, leadSource };
      case 2: return { verificationStatus: VerificationFilter.NOT_VERIFIED, leadSource };
      default: return { leadSource };
    }
  }

  async getLeads(
    page = this.currentPage,
    filters?: { verificationStatus?: VerificationFilter; leadSource?: LeadSource }
  ) {
    try {
      this.currentPage = page;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          page: this.currentPage,
          tab: this.activeNav,
          pageSize: this.pageSize,
          searchQuery: this.searchQuery,
          leadSource: this.selectedLeadSource || null
        },
        queryParamsHandling: 'merge'
      });

      let response = await this.adminService.getLeads(
        page,
        this.pageSize,
        filters || {},
        this.searchQuery,
        this.currentSort
      );
      this.leadsPaginationInfo =
        this.tableUtils.convertPageResponseToPaginationInfo(response);
      this.leads = response.content;

    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }

  onTabChange(tab: number) {
    this.activeNav = tab;
    this.viewStatus = ViewStatus.LOADING;

    let filters: { verificationStatus?: VerificationFilter; leadSource?: LeadSource } = {
      leadSource: this.selectedLeadSource || undefined
    };

    switch (tab) {
      case 0:
        filters = { leadSource: this.selectedLeadSource || undefined };
        break;
      case 1:
        filters.verificationStatus = VerificationFilter.VERIFIED;
        break;
      case 2:
        filters.verificationStatus = VerificationFilter.NOT_VERIFIED;
        break;
      default:
        filters = { leadSource: this.selectedLeadSource || undefined };
        break;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: 0, tab: this.activeNav, leadSource: this.selectedLeadSource || null },
      queryParamsHandling: 'merge'
    });

    this.getLeads(0, filters).then(() => {
      this.viewStatus = ViewStatus.READY;
    });
  }

  private readonly leadSourceStatuses: AvailableBadgeStatus[] = [
    { value: LeadSource.WEBSITE, label: 'Website', color: 'bg-primary text-white' },
    { value: LeadSource.FACEBOOK, label: 'Facebook', color: 'bg-info text-white' },
  ];

  private readonly hasRequestStatuses: AvailableBadgeStatus[] = [
    { value: 'true', label: 'Yes', color: 'bg-success text-white' },
    { value: 'false', label: 'No', color: 'bg-danger text-white' },
  ];

  setColumns() {
    this.columns = [
      {
        label: 'ID',
        attributeName: 'id',
        sortable: true,
        attribute: (data: Lead) => data.id,
        attributeType: AttributeType.STRING,
      },
      {
        label: 'Patient',
        attributeName: 'firstname',
        sortable: true,
        component: LeadNameComponent,
        componentInjector: this.tableUtils.createInjector(this.injector, {}),
      },
      {
        label: 'Email',
        attributeName: 'email',
        sortable: true,
        component: LeadEmailTableComponent,
        componentInjector: this.tableUtils.createInjector(this.injector, {}),
      },
      {
        label: 'Phone Number',
        attributeName: 'phoneNumber',
        component: LeadPhoneTableComponent,
        componentInjector: this.tableUtils.createInjector(this.injector, {}),
      },


      {
        label: 'Source',
        attributeName: 'leadSource',
        sortable: true,
        component: BadgeTableComponent,
        componentInjector: this.tableUtils.createInjector(this.injector, {
          availableStatuses: this.leadSourceStatuses,
          status: (data: Lead) => data.leadSource ?? '',
        }),
        attribute: (data: Lead) => data.leadSource ?? '',
        attributeType: AttributeType.STRING,
      },

      {
        label: 'Created at',
        attributeName: 'createdAt',
        sortable: true,
        attribute: (data: Lead) => DateUtils.formatDateTime(data.createdAt),
        attributeType: AttributeType.STRING,
      },
      {
        component: QuickActionsTableComponent,
        componentInjector: this.tableUtils.createInjector(this.injector, {
          items: [
            {
              icon: 'info-square-rounded',
              label: "",
              onClick: (data: Lead) => this.navigateToDetails(data),
            },
          ]
        })
      }
    ];
  }

  navigateToDetails(lead: Lead) {
    const modalRef = this.modalService.open(LeadDetailsComponent, {
      backdrop: true,
      centered: false,
      size: 'lg',
      scrollable: true,
    });
    modalRef.componentInstance.leadId = lead.id;
    modalRef.result.then(async (result) => {
      if (result) {
      }
    });
  }
}
